import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';

// Prime-React Components
import { Button } from 'primereact/button';
import { classNames } from 'primereact/utils';
import { RadioButton } from 'primereact/radiobutton';
import { InputSwitch } from 'primereact/inputswitch';
import { confirmDialog } from 'primereact/confirmdialog';

// Component
import Label from '../../components/Label';
import { CustomeTooltip } from '../../components/ActionButtons';
import CustomButton from '../../components/CustomButton';
import AvtarImage from './AvtarImage';
import { TextAreaWithCounterLimit } from '../../components/TextAreaWithCounterLimit';
import FormikTextField from '../../components/form/FormikTextField';
import FormikInputNumber from '../../components/form/FormikInputNumber';
import FormikMultiSelect from '../../components/form/FormikMultiSelect';
import FormikAutocomplete from '../../components/form/FormikAutocomplete';

// Constants
import { ROUTES, BUTTON_TYPES, LABEL_TYPES, ROLES } from '../../constants/common';

// Redux-Actions
import {
  editUserProfileDetails,
  getUserPermissionRequestAction,
  getUserProfileDetails,
  userPermissionChangeRequestAction
} from '../../redux/actions/profileDetails';
import { getInterviewTechnology } from '../../redux/actions/hr';

// Redux-slices
import { notificationPermissionAlertClosed } from '../../redux/slices/notification';

// Utils
import { getFormErrorMessage, isFormFieldValid, wordCapitalize } from '../../utils/common';
import { onSuggestSkils } from '../../utils/pageUtils';

//Apis
import UserProfileUpdatePassword from './UserProfileUpdatePassword';
import { userProfileSchema } from '../../schema/userProfile.Schema';

const pathBase = `${process.env.REACT_APP_API_ENDPOINT}/public/uploads/profile`;
const UserProfile = () => {
  // Redux hooks
  const dispatch = useDispatch();
  const location = useLocation();
  const interviewTechnologyType = useSelector((state) => state.hrs.interviewTechnologyType);
  const { userId, userType, firstName, lastName, userProfile } = useSelector((state) => state.auth);
  const { isLoading } = useSelector((state) => state.apiStatus);
  const profileDetails = useSelector((state) => state.profileDetails);
  const portal = location?.pathname?.toString().split('/').at(1);

  // react-router-dom hook
  const navigate = useNavigate();

  // React useState hooks
  const [softSkilSuggestion, setSoftSkilSuggestion] = useState([]);
  const [imagePrev, setImagePrev] = useState(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [redirection, setRedirection] = useState(false);
  const [showChangePasswordPopup, setShowChangePasswordPopup] = useState(false);
  const [userProfileData, setUserProfileData] = useState({
    isAdmin: false,
    isHR: false,
    firstName: '',
    lastName: '',
    email: '',
    pmEmail: '',
    mobile: '',
    gender: 1,
    address: '',
    yearsOfExperience: 0,
    monthsOfExperience: 0,
    techStack: [],
    skills: [],
    removeImage: false,
    isPracticalAllow: false
  });
  const [isNotificationEnabled, setIsNotificationEnabled] = useState(true);

  // React useRef hook
  const fileRef = useRef(null);

  const userName = `${wordCapitalize(firstName || '')} ${wordCapitalize(lastName || '')}`;
  const { UserPermissions } = profileDetails;

  useEffect(() => {
    dispatch(getUserProfileDetails());
    dispatch(getInterviewTechnology());
    dispatch(getUserPermissionRequestAction());
  }, [dispatch]);

  const handleSuggestion = (event) => {
    setSoftSkilSuggestion(onSuggestSkils(event));
  };

  useEffect(() => {
    if (UserPermissions) {
      setIsNotificationEnabled(UserPermissions.IsNotificationEnable);
    }
  }, [UserPermissions]);

  useEffect(() => {
    if (redirection) {
      switch (userType) {
        case ROLES.ADMIN:
          return navigate(ROUTES.ADMIN.DASHBOARD);
        case ROLES.HR:
          return navigate(ROUTES.HR.DASHBOARD);
        case ROLES.INTERVIEWER:
          return navigate(ROUTES.INTERVIEWER.DASHBOARD);
        case ROLES.HR_AND_INTERVIEWER:
          return navigate(portal === 'hr' ? ROUTES.HR.DASHBOARD : ROUTES.INTERVIEWER.DASHBOARD);
      }
    }
  }, [redirection, userType, navigate, dispatch]);

  useEffect(() => {
    if (profileDetails && Object.keys(profileDetails).length !== 0) {
      setUserProfileData({
        isAdmin: userType === ROLES.ADMIN,
        isHR: userType === ROLES.HR,
        isInterviewer: userType === ROLES.INTERVIEWER,
        firstName: `${profileDetails.FirstName}`,
        lastName: `${profileDetails.LastName}`,
        email: `${profileDetails.Email || ''}`,
        pmEmail: `${profileDetails?.ProjectManagerEmail || ''}`,
        mobile: profileDetails?.Mobile || '',
        gender: parseInt(profileDetails.Gender),
        address: `${profileDetails.Address || ''}`,
        monthsOfExperience: `${profileDetails.MonthsOfExperience || 0}`,
        yearsOfExperience: `${profileDetails.YearsOfExperience || 0}`,
        techStack: profileDetails?.TechStack || [],
        isPracticalAllow: !!profileDetails?.IsPracticalAllow,
        skills: profileDetails?.UMap?.length
          ? profileDetails?.UMap?.map((tech) => tech.MapT.ID)
          : []
      });
    }
  }, [profileDetails, userType]);

  const formik = useFormik({
    initialValues: userProfileData,
    validationSchema: userProfileSchema,
    onSubmit: (values) => handleSubmit(values),
    enableReinitialize: true
  });

  const handleSubmit = (values) => {
    try {
      const formData = {
        firstName: values.firstName,
        lastName: values.lastName,
        pmEmail: values.pmEmail,
        gender: values.gender,
        address: values.address,
        mobile: values.mobile,
        isPracticalAllow: values.isPracticalAllow,
        removeImage,
        isHR: userType === ROLES.HR,
        techStack: values.techStack,
        skills: values.skills
      };
      let postData = new FormData();
      Object.keys(formData).forEach((el) => {
        if (el === 'skills') {
          for (var i = 0; i < formData[el].length; i++) {
            postData.append('skills[]', formData[el][i]);
          }
        } else if (typeof el === 'string') {
          postData.append(el, formData[el]);
        } else {
          postData.append(el, JSON.stringify(formData[el]));
        }
      });
      imagePrev && postData.append('profileImage', values.profileImage);
      !userProfileData.isAdmin && postData.append('monthsOfExperience', values.monthsOfExperience);
      !userProfileData.isAdmin && postData.append('yearsOfExperience', values.yearsOfExperience);
      // Call the dispatch method to update the user profile.
      dispatch(editUserProfileDetails(postData));
    } catch (error) {
      console.log(error);
    }
  };

  const imageName = profileDetails?.UserProfile || '';
  const encodedPath = imageName
    .split('/')
    .map((p) => encodeURIComponent(p))
    .join('/');

  useEffect(() => {
    if (removeImage === true) {
      formik.setFieldValue('profileImage', null);
      setImagePrev(null);
    }
  }, [removeImage]);

  const removeImageConfirmation = () => {
    confirmDialog({
      header: 'Delete',
      message: 'Are you sure you want to delete profile image?',
      icon: 'pi pi-exclamation-triangle',
      accept: () => setRemoveImage(true)
    });
  };

  return (
    <>
      <CustomeTooltip />
      <div className="user-profile-wrapper">
        <form onSubmit={formik.handleSubmit} className="form-main" encType="multipart/form-data">
          <div className="left-col">
            <div className="form-col">
              <div className="profile-box">
                <div className="box-inner">
                  <div className="profile-img">
                    {profileDetails?.UserProfile && !imagePrev && !removeImage ? (
                      <img
                        src={`${pathBase}/${userId}/${encodedPath}`}
                        alt="profile"
                        className="user-img"
                      />
                    ) : !imagePrev || removeImage ? (
                      <AvtarImage className="user-img" firstName={firstName} lastName={lastName} />
                    ) : (
                      <img src={imagePrev} alt="profile" className="user-img" />
                    )}
                    <input
                      hidden
                      ref={fileRef}
                      type="file"
                      id="profileImage"
                      name="profileImage"
                      onChange={(e) => {
                        if (e.target.files[0]) {
                          formik.setFieldValue('profileImage', e.target.files[0]);
                          if (
                            ['image/png', 'image/jpeg', 'image/jpg'].includes(
                              e.target.files[0].type
                            )
                          )
                            setImagePrev(URL.createObjectURL(e.target.files[0]));
                          else setImagePrev(false);
                          setRemoveImage(false);
                        }
                      }}
                      accept="image/png, image/jpeg ,image/jpg"
                      className={classNames({
                        'p-invalid': isFormFieldValid(formik, 'profileImage')
                      })}
                    />

                    <div className="user-details">
                      <div className="user-name">{userName}</div>
                    </div>
                  </div>
                  <div className="edit-delete-btn">
                    <Button
                      type="button"
                      className="border-none edit-btn"
                      icon="pi pi-pencil"
                      label={!userProfile ? 'Upload' : 'Change'}
                      onClick={() => {
                        fileRef.current.click();
                      }}
                    />
                    <Button
                      disabled={!userProfile ? 'disabled' : ''}
                      className="border-none delete-btn"
                      icon="pi pi-trash"
                      label="Delete"
                      type="button"
                      onClick={() => removeImageConfirmation()}
                    />
                  </div>
                  {
                    <small className="p-error">
                      {formik.errors['profileImage'] && formik.errors['profileImage']}
                    </small>
                  }
                </div>
              </div>
              <div className="notification-outer">
                {userType === ROLES.HR ||
                userType === ROLES.INTERVIEWER ||
                userType === ROLES.HR_AND_INTERVIEWER ? (
                  <div className="notification-div">
                    <Label htmlFor="Notification" text={LABEL_TYPES.NOTIFICATION} />
                    <InputSwitch
                      checked={isNotificationEnabled}
                      onChange={(e) => {
                        setIsNotificationEnabled(e.value);
                        if (e.value) {
                          dispatch(notificationPermissionAlertClosed(false));
                        }
                        dispatch(
                          userPermissionChangeRequestAction({
                            isNotificationEnable: e.value,
                            pushNotification: true
                          })
                        );
                      }}
                    />
                  </div>
                ) : (
                  ''
                )}
                <div className="notification-div reset-password-div">
                  <div
                    className="text-right font-medium no-underline text-blue-500 cursor-pointer primary-link"
                    onClick={() => setShowChangePasswordPopup(true)}>
                    Change Password
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="right-col white-box">
            <div className="form-row-wrapper">
              <div className="form-col">
                <div className="form-group-outer">
                  <div className="custom-form-group">
                    <FormikTextField
                      labelText={LABEL_TYPES.FIRST_NAME}
                      isMandatory
                      formik={formik}
                      name="firstName"
                      placeholder={LABEL_TYPES.FIRST_NAME + ' *'}
                    />
                  </div>
                </div>
              </div>
              <div className="form-col">
                <div className="form-group-outer">
                  <div className="custom-form-group">
                    <FormikTextField
                      labelText={LABEL_TYPES.LAST_NAME}
                      isMandatory
                      formik={formik}
                      name="lastName"
                      placeholder={LABEL_TYPES.LAST_NAME + ' *'}
                    />
                  </div>
                </div>
              </div>
              <div className="form-col">
                <div className="form-group-outer">
                  <div className="custom-form-group">
                    <FormikTextField
                      labelText={LABEL_TYPES.EMAIL}
                      isMandatory
                      formik={formik}
                      name="email"
                      placeholder={LABEL_TYPES.EMAIL + ' *'}
                      disabled
                    />
                  </div>
                </div>
              </div>
              <div className="form-col">
                <div className="form-group-outer">
                  <div className="custom-form-group">
                    <FormikTextField
                      labelText={LABEL_TYPES.MOBILE}
                      isMandatory
                      formik={formik}
                      name="mobile"
                      placeholder={LABEL_TYPES.MOBILE + ' *'}
                      keyfilter="int"
                    />
                  </div>
                </div>
              </div>
              <div className="form-col">
                <div className="form-group-outer">
                  <div className="custom-form-group">
                    <TextAreaWithCounterLimit
                      placeholder={LABEL_TYPES.ADDRESS}
                      labelText={LABEL_TYPES.ADDRESS}
                      value={formik.values.address}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      errorMessage={formik.touched['address'] ? formik.errors['address'] : ''}
                      className={classNames({
                        'p-invalid': isFormFieldValid(formik, 'address')
                      })}
                      rowsValue={1}
                      id="address"
                      name="address"
                      htmlFor="address"
                      isMandatory
                      validationOnMinLimit
                    />
                  </div>
                </div>
              </div>
              <div className="form-col">
                <div className="form-group-outer">
                  <div className="custom-form-group margin-bottom">
                    <Label htmlFor="gender" text={LABEL_TYPES.GENDER} isMandatory isBold />
                    <div className="radio-list-wrapper">
                      <div className="form-group-outer-radiobutton">
                        <RadioButton
                          inputId="male"
                          value={1}
                          name="gender"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          checked={formik.values.gender === 1}
                        />
                        <Label htmlFor="male" text={LABEL_TYPES.MALE} isBold />
                      </div>
                      <div className="form-group-outer-radiobutton">
                        <RadioButton
                          inputId="female"
                          value={2}
                          name="gender"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          checked={formik.values.gender === 2}
                        />
                        <Label htmlFor="female" text={LABEL_TYPES.FEMALE} isBold />
                      </div>
                    </div>
                  </div>
                  {(userProfileData.isInterviewer || userType === ROLES.HR_AND_INTERVIEWER) && (
                    <div className="custom-form-group horizontal-switch margin-bottom">
                      <Label for="isPracticalAllow" text={LABEL_TYPES.PRACTICAL_ROUND} />
                      <InputSwitch
                        checked={formik.values.isPracticalAllow}
                        onChange={(e) => {
                          formik.setFieldValue('isPracticalAllow', e.value);
                        }}
                      />
                    </div>
                  )}
                  {(userProfileData.isInterviewer || userType === ROLES.HR_AND_INTERVIEWER) && (
                    <div className="custom-form-group horizontal-switch">
                      <Label for="IsPracticalAdmin" text={LABEL_TYPES.PRACTICAL_ADMIN} />
                      <InputSwitch checked={!!profileDetails.IsPracticalAdmin} disabled />
                    </div>
                  )}
                  {getFormErrorMessage(formik, 'gender')}
                </div>
              </div>

              {!userProfileData.isAdmin ? (
                <>
                  <div className="form-col full-width">
                    <div className="form-group-outer">
                      <div className="custom-form-group autocomplete">
                        {userProfileData.isInterviewer || userType === ROLES.HR_AND_INTERVIEWER ? (
                          <>
                            <FormikMultiSelect
                              labelText={LABEL_TYPES.TECHNICAL_SKILLS}
                              formik={formik}
                              name="skills"
                              placeholder={LABEL_TYPES.TECHNICAL_SKILLS}
                              optionLabel="name"
                              optionValue="value"
                              options={
                                interviewTechnologyType?.length
                                  ? interviewTechnologyType.map((tech) => ({
                                      name: tech.Technology,
                                      value: tech.ID
                                    }))
                                  : []
                              }
                              className="w-full"
                            />
                          </>
                        ) : (
                          <>
                            <FormikAutocomplete
                              labelText={LABEL_TYPES.TECHNICAL_SKILLS}
                              formik={formik}
                              name="techStack"
                              placeholder={LABEL_TYPES.SOFT_SKILLS}
                              multiple
                              suggestions={softSkilSuggestion}
                              completeMethod={handleSuggestion}
                            />
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="form-col">
                    <div className="form-group-outer">
                      <div className="custom-form-group">
                        <FormikInputNumber
                          labelText={LABEL_TYPES.YEAR_EXPERIENCE}
                          isMandatory
                          formik={formik}
                          name="yearsOfExperience"
                          placeholder={LABEL_TYPES.YEAR_EXPERIENCE}
                          onValueChange={(e) => formik.setFieldValue('yearsOfExperience', e.value)}
                          min={0}
                          max={100}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="form-col">
                    <div className="form-group-outer">
                      <div className="custom-form-group">
                        <FormikInputNumber
                          labelText={LABEL_TYPES.MONTH_EXPERIENCE}
                          isMandatory
                          formik={formik}
                          name="monthsOfExperience"
                          placeholder={LABEL_TYPES.MONTH_EXPERIENCE}
                          onValueChange={(e) => formik.setFieldValue('monthsOfExperience', e.value)}
                          min={0}
                          max={11}
                        />
                      </div>
                    </div>
                  </div>
                  {userProfileData.isInterviewer && (
                    <div className="form-col full-width">
                      <div className="form-group-outer">
                        <div className="custom-form-group">
                          <FormikTextField
                            labelText={LABEL_TYPES.PM_EMAIL}
                            isMandatory
                            formik={formik}
                            name="pmEmail"
                            placeholder={LABEL_TYPES.PM_EMAIL + ' *'}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                ''
              )}
            </div>
            <div className="form-btn-wrapper">
              <CustomButton
                type="submit"
                variant="contained"
                className="ims-blue-btn"
                disabled={isLoading}>
                {BUTTON_TYPES.SAVE}
              </CustomButton>
              <CustomButton
                variant="contained"
                onClick={() => setRedirection(true)}
                color="error"
                className="gray-btn border-btn">
                {BUTTON_TYPES.CANCEL}
              </CustomButton>
            </div>
          </div>
        </form>
      </div>

      <UserProfileUpdatePassword
        show={showChangePasswordPopup}
        onHide={(value) => setShowChangePasswordPopup(value)}
      />
    </>
  );
};

export default UserProfile;
